// Source-backed lesson outlines for the paid Illinois Broker Prep study system.
// These lessons teach exam concepts in plain English; students should still use
// the linked source material and their required pre-license course for current rules.

export const PSI_BULLETIN_URL = "https://test-takers.psiexams.com/api/content/bulletin/6251";
export const ILLINOIS_LICENSE_ACT_URL =
  "https://www.ilga.gov/legislation/ilcs/ilcs3.asp?ActID=1363&ChapterID=24";
export const ILLINOIS_ADMIN_CODE_URL =
  "https://www.ilga.gov/agencies/JCAR/EntirePart?titlepart=06801450";
export const ILLINOIS_DISCLOSURE_ACT_URL =
  "https://ilga.gov/Legislation/ILCS/Articles?ActID=2152&Chapter=PROPERTY&ChapterID=62&MajorTopic=RIGHTS-AND-REMEDIES";

const PSI_SOURCE = "PSI Candidate Information Bulletin, National Broker outline";

export const LEARN_CURRICULUM = [
  {
    id: "illinois-licensing",
    number: "01",
    category: "Illinois core",
    title: "Licensing, sponsorship, and who may practice",
    duration: "6 min",
    outlineSection: "Licensing Requirements",
    sourceReference: "225 ILCS 454, Art. 5",
    sourceUrl: ILLINOIS_LICENSE_ACT_URL,
    summary:
      "Start with Illinois language. The entry-level full license is Broker, and licensed activity is tied to proper sponsorship.",
    objectives: [
      "Separate Broker, Managing Broker, and Leasing Agent roles.",
      "Recognize why sponsorship matters before licensed activity begins or resumes.",
      "Spot license-status distractors in Illinois fact patterns.",
    ],
    blocks: [
      {
        heading: "Illinois uses its own license vocabulary",
        body:
          "Illinois calls the entry-level full real estate license a Broker. A Managing Broker is the higher supervisory role. Do not let a generic question choice such as 'salesperson license' pull you away from the Illinois terminology.",
      },
      {
        heading: "Sponsorship is an exam-level control point",
        body:
          "A Broker practices through a valid sponsoring relationship. When a fact pattern changes a broker's firm or sponsorship, slow down and ask whether the licensee is currently authorized to perform licensed activity.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A Broker leaves a firm on Friday and has not registered a new sponsorship on Monday. The test is usually asking about the ability to practice now, not whether the person permanently lost the license.",
    },
    traps: [
      "Treating Broker and Managing Broker as interchangeable.",
      "Assuming a broker may practice independently while looking for a new sponsor.",
    ],
    terms: [
      ["Broker", "Illinois's entry-level full real estate license."],
      ["Managing Broker", "The supervisory license level used to manage brokerage activity and sponsored licensees."],
      ["Sponsorship", "The registered relationship that connects a licensee to a sponsoring brokerage."],
    ],
  },
  {
    id: "illinois-license-act",
    number: "02",
    category: "Illinois core",
    title: "The Illinois License Act and brokerage responsibilities",
    duration: "7 min",
    outlineSection: "Illinois Real Estate License Act",
    sourceReference: "225 ILCS 454, Arts. 10 and 15",
    sourceUrl: ILLINOIS_LICENSE_ACT_URL,
    summary:
      "Use the License Act as the framework for brokerage duties, supervision, conduct, and discipline questions.",
    objectives: [
      "Identify when a question is testing a license-law duty rather than a generic business preference.",
      "Separate individual licensee conduct from supervising-broker responsibility.",
      "Read a fact pattern for the role, duty, and consequence it is actually testing.",
    ],
    blocks: [
      {
        heading: "Start with the role in the fact pattern",
        body:
          "Illinois questions often name a Broker, Managing Broker, sponsoring brokerage, or unlicensed assistant. The role tells you which duties and limits matter. Read it before you jump to the answer choices.",
      },
      {
        heading: "The state portion rewards precise duties",
        body:
          "The best answer is usually the one that follows the Illinois framework for supervision, representations, records, or licensed activity. Broad ethical statements can sound attractive but may not answer the statutory issue being tested.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "When a question asks who may open a brokerage or supervise licensees, identify the required supervisory role before considering business-form details.",
    },
    traps: [
      "Choosing the most moral-sounding answer instead of the legally accurate one.",
      "Confusing a firm requirement with an individual license requirement.",
    ],
    terms: [
      ["Sponsoring broker", "The brokerage relationship through which a licensee performs licensed activity."],
      ["Supervision", "The oversight responsibility tested when a Managing Broker or sponsoring brokerage is involved."],
      ["Discipline", "The licensing consequence framework the Department may apply for violations."],
    ],
  },
  {
    id: "illinois-additional-laws",
    number: "03",
    category: "Illinois core",
    title: "Escrow, records, and additional Illinois laws",
    duration: "7 min",
    outlineSection: "Additional Illinois Laws & Regulations",
    sourceReference: "68 Ill. Adm. Code 1450; Illinois transaction statutes",
    sourceUrl: ILLINOIS_ADMIN_CODE_URL,
    summary:
      "This area is where operational details matter: escrow handling, brokerage records, and Illinois-specific transaction rules.",
    objectives: [
      "Recognize an escrow-account question immediately.",
      "Separate client money from brokerage operating funds.",
      "Treat a detailed rule question as a source-and-structure problem, not a guess.",
    ],
    blocks: [
      {
        heading: "Escrow is about custody and safeguards",
        body:
          "When earnest money or other client funds are involved, focus on the required account, the institution, and the separation of client money from the brokerage's own funds. The exact rule matters more than what seems convenient.",
      },
      {
        heading: "Use the noun in the question",
        body:
          "Illinois questions may test a deposit, a lease-related obligation, a record, or a brokerage procedure. Underline what is being handled, who holds it, and which rule controls the timing or account.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A question gives several possible places for a sponsoring broker's escrow account. Eliminate answers that ignore the federally insured depository requirement or mix client money with business money.",
    },
    traps: [
      "Choosing an account solely because it earns interest.",
      "Assuming any financial institution is acceptable without checking the stated safeguard.",
    ],
    terms: [
      ["Escrow account", "An account used to hold money belonging to others in connection with a transaction."],
      ["Earnest money", "A buyer's deposit showing serious intent under a purchase agreement."],
      ["Commingling", "Improperly mixing client funds with a broker's own funds."],
    ],
  },
  {
    id: "illinois-disclosures",
    number: "04",
    category: "Illinois core",
    title: "Illinois disclosures and consumer protection",
    duration: "7 min",
    outlineSection: "Disclosures",
    sourceReference: "765 ILCS 77; 420 ILCS 46; 42 U.S.C. 4852d",
    sourceUrl: ILLINOIS_DISCLOSURE_ACT_URL,
    summary:
      "Disclosure questions test who must disclose, what is being disclosed, and what a disclosure does not promise.",
    objectives: [
      "Distinguish a disclosure from a warranty or guarantee.",
      "Recognize material-defect, radon, and lead-based-paint fact patterns.",
      "Read for the property type, the known condition, and the statute named by the facts.",
    ],
    blocks: [
      {
        heading: "Disclosure is information, not insurance",
        body:
          "A disclosure form communicates known information under the applicable rule. It does not turn the seller, broker, or form into a guarantee that every part of a property is perfect.",
      },
      {
        heading: "Match the fact to the disclosure system",
        body:
          "Illinois questions may combine a property condition, a federal lead-based-paint trigger, or radon awareness. Identify the age or type of property and the condition described before selecting an answer.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A seller's disclosure reports a known issue. The correct answer usually recognizes the disclosure obligation without claiming it guarantees the buyer against every future problem.",
    },
    traps: [
      "Calling a disclosure a warranty.",
      "Ignoring the distinction between a known condition and a future inspection finding.",
    ],
    terms: [
      ["Material defect", "A condition that can matter to a reasonable buyer's decision about property."],
      ["Seller disclosure", "Information a seller provides about known property conditions under the applicable law."],
      ["Lead-based paint disclosure", "Federal disclosure requirements that can apply to qualifying older residential property."],
    ],
  },
  {
    id: "property-ownership",
    number: "05",
    category: "National foundation",
    title: "Property ownership and estates in land",
    duration: "6 min",
    outlineSection: "Property Ownership",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Ownership questions ask what interest exists, who holds it, and which rights travel with the property.",
    objectives: [
      "Separate real property from personal property.",
      "Recognize estates, co-ownership, and encumbrances.",
      "Use the bundle-of-rights idea to reason through ownership questions.",
    ],
    blocks: [
      {
        heading: "Start with the interest, not the label",
        body:
          "Ask whether the fact pattern describes ownership, possession, a lien, an easement, or a leasehold. The right being transferred or limited is usually the real issue.",
      },
      {
        heading: "The bundle of rights is a reasoning tool",
        body:
          "Ownership can involve possession, control, enjoyment, exclusion, and disposition. A question may remove or share one right without eliminating the entire ownership interest.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "If a neighboring parcel has a right to cross land, the question is likely testing an easement rather than ownership of the land itself.",
    },
    traps: [
      "Treating every property-related right as ownership.",
      "Mixing up a lien, an easement, and a leasehold interest.",
    ],
    terms: [
      ["Estate in land", "The degree, nature, and duration of a person's interest in real property."],
      ["Encumbrance", "A claim, charge, or restriction that affects title or use of property."],
      ["Easement", "A nonpossessory right to use another person's land for a stated purpose."],
    ],
  },
  {
    id: "land-use",
    number: "06",
    category: "National foundation",
    title: "Land use, zoning, and government controls",
    duration: "6 min",
    outlineSection: "Land Use Controls",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Land-use questions turn on the source of the restriction and whether the property use conforms to it.",
    objectives: [
      "Identify zoning, deed restrictions, and government powers.",
      "Separate a variance from a zoning change.",
      "Recognize a legal nonconforming use.",
    ],
    blocks: [
      {
        heading: "Find the source of the restriction",
        body:
          "Government zoning and private deed restrictions are not the same thing. The exam often gives a clue about who imposed the restriction and what remedy or change is being requested.",
      },
      {
        heading: "A variance is not a new zoning map",
        body:
          "A variance is typically relief from applying an existing rule to a particular property. A zoning amendment changes the rule itself. Keep the requested result separate from the reason the owner wants it.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A use was lawful before a zoning change and continues afterward. That fact pattern points toward a nonconforming use, not an automatic violation.",
    },
    traps: [
      "Calling every exception a variance.",
      "Confusing a public zoning control with a private covenant.",
    ],
    terms: [
      ["Zoning", "A local government's regulation of land use and development."],
      ["Variance", "Permission to depart from a zoning requirement for a particular property."],
      ["Nonconforming use", "A use that was lawful when established but does not match a later rule."],
    ],
  },
  {
    id: "valuation",
    number: "07",
    category: "National foundation",
    title: "Valuation, appraisal, and market value",
    duration: "6 min",
    outlineSection: "Valuation",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Value questions reward clean distinctions: price is paid, cost is spent, and value is an opinion under stated assumptions.",
    objectives: [
      "Separate market value, price, and cost.",
      "Recognize the main appraisal approaches.",
      "Identify the role of a comparative market analysis without calling it an appraisal.",
    ],
    blocks: [
      {
        heading: "Use the word the question gives you",
        body:
          "A property can sell above or below a value opinion. Do not substitute price for value or cost for value just because the numbers look related.",
      },
      {
        heading: "Approach follows property and data",
        body:
          "Sales comparison relies on comparable sales. Cost considers replacement or reproduction cost and depreciation. Income capitalization focuses on income-producing property. Look for the data source in the fact pattern.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A broker compares recent sales to help a seller list a home. That is a pricing analysis, not automatically a formal appraisal.",
    },
    traps: [
      "Treating a CMA and an appraisal as identical services.",
      "Equating a contract price with market value in every case.",
    ],
    terms: [
      ["Market value", "A value opinion formed under stated market assumptions."],
      ["Comparable sale", "A recently sold property used to help estimate value."],
      ["Depreciation", "A loss in value from physical, functional, or external causes."],
    ],
  },
  {
    id: "financing",
    number: "08",
    category: "National foundation",
    title: "Financing, mortgages, and loan math",
    duration: "7 min",
    outlineSection: "Financing",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Financing questions test the relationship among borrower, lender, note, mortgage, value, and risk.",
    objectives: [
      "Separate a promissory note from the security instrument.",
      "Recognize loan-to-value and down-payment relationships.",
      "Read amortization, interest, and foreclosure vocabulary in context.",
    ],
    blocks: [
      {
        heading: "Debt and security are different documents",
        body:
          "The promissory note is evidence of the debt. The mortgage or deed of trust is the security instrument that creates an interest in the real property. Tests often swap these labels on purpose.",
      },
      {
        heading: "Make the percentages talk",
        body:
          "Loan-to-value compares the loan amount with value or price as stated in the problem. Before calculating, identify the base number and write the relationship in plain language.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A loan is 80% of the stated value. The remaining 20% is not automatically the buyer's cash down payment until the problem confirms the purchase and financing facts.",
    },
    traps: [
      "Calling the note the lien.",
      "Using the wrong base amount in a loan-to-value calculation.",
    ],
    terms: [
      ["Promissory note", "The borrower's written promise to repay a debt."],
      ["Mortgage", "A security instrument that creates a lien on real property for a loan."],
      ["Loan-to-value ratio", "The loan amount expressed as a percentage of value or price, as the question states."],
    ],
  },
  {
    id: "contracts",
    number: "09",
    category: "National foundation",
    title: "Contracts, offers, acceptance, and remedies",
    duration: "7 min",
    outlineSection: "Contracts",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Contract questions are about formation, enforceability, performance, and what a change to an offer actually does.",
    objectives: [
      "Recognize the basic elements of a contract.",
      "Separate a counteroffer from an acceptance.",
      "Read contingencies and default questions for the party and condition involved.",
    ],
    blocks: [
      {
        heading: "Match the response to the offer",
        body:
          "An acceptance mirrors the offer. A change to a material term is usually a counteroffer, which changes the negotiating posture. The order of communication in the fact pattern matters.",
      },
      {
        heading: "Contingencies allocate uncertainty",
        body:
          "A contingency sets a condition that must be satisfied, waived, or handled under the agreement. Do not assume a contingency gives every party the same right at every time.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A buyer changes the closing date while signing an offer. The question is testing whether the response accepted the original terms or created a counteroffer.",
    },
    traps: [
      "Calling a changed offer an acceptance.",
      "Ignoring who has the right or obligation under a contingency.",
    ],
    terms: [
      ["Consideration", "Something of value exchanged to support a contract."],
      ["Counteroffer", "A response that changes an offer and proposes different terms."],
      ["Contingency", "A condition in an agreement that affects performance or obligation."],
    ],
  },
  {
    id: "agency",
    number: "10",
    category: "National foundation",
    title: "Agency, clients, customers, and duties",
    duration: "7 min",
    outlineSection: "Agency",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Agency questions ask who is represented, what duty is owed, and what must be disclosed before a conflict appears.",
    objectives: [
      "Separate a client from a customer.",
      "Recognize fiduciary duties and agency disclosure issues.",
      "Read dual-agency or conflict questions for informed consent requirements in the facts.",
    ],
    blocks: [
      {
        heading: "Representation changes the duty",
        body:
          "A client is represented by the licensee; a customer receives services without the same agency relationship. Identify the relationship before deciding what confidentiality, loyalty, or disclosure duties apply.",
      },
      {
        heading: "Conflicts do not disappear because everyone wants the deal",
        body:
          "When one brokerage or licensee is connected to both sides, the exam usually tests disclosure and consent. Read the fact pattern for who knows what and when they learned it.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A buyer shares a negotiation limit with an agent. The question is likely testing whether that information may be revealed, not whether the sale would close faster.",
    },
    traps: [
      "Treating every prospect as a client.",
      "Assuming a broker may disclose confidential bargaining information to help the transaction close.",
    ],
    terms: [
      ["Client", "A party represented by a licensee in an agency relationship."],
      ["Customer", "A party who receives service without being represented as a client."],
      ["Fiduciary duty", "A heightened duty of loyalty, care, obedience, disclosure, accounting, and confidentiality owed in an agency relationship."],
    ],
  },
  {
    id: "property-disclosures",
    number: "11",
    category: "National foundation",
    title: "Property condition and federal disclosure concepts",
    duration: "6 min",
    outlineSection: "Property Disclosures",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "National disclosure questions test known material facts, the scope of a licensee's role, and federal disclosures tied to particular properties.",
    objectives: [
      "Distinguish a material fact from a personal opinion.",
      "Recognize the difference between a disclosure duty and an inspection result.",
      "Spot property-age and hazard clues in federal disclosure questions.",
    ],
    blocks: [
      {
        heading: "Known facts drive the analysis",
        body:
          "When a licensee knows a fact that could materially affect a buyer's decision, the question usually turns on the obligation to handle that information correctly. Facts, not speculation, should drive your answer.",
      },
      {
        heading: "Look for the trigger",
        body:
          "Federal disclosure questions often give a property age, hazard, or document clue. Use that trigger rather than guessing based on whether the property looks risky.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A licensee learns of a known condition after a listing begins. The question is testing what to do with known material information, not whether the licensee can diagnose the property.",
    },
    traps: [
      "Confusing a licensee's disclosure role with a home inspector's role.",
      "Treating every buyer concern as a legally material fact.",
    ],
    terms: [
      ["Material fact", "Information that could matter to a reasonable party's transaction decision."],
      ["Latent defect", "A hidden defect that may not be discoverable through ordinary observation."],
      ["Disclosure", "Providing required or material information to the appropriate party."],
    ],
  },
  {
    id: "property-management",
    number: "12",
    category: "National foundation",
    title: "Property management and leasing fundamentals",
    duration: "6 min",
    outlineSection: "Property Management",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Property-management questions combine owner objectives, tenant relations, leases, records, and fair-housing awareness.",
    objectives: [
      "Identify the property manager's role for the owner.",
      "Separate a leasehold interest from ownership.",
      "Recognize lease, maintenance, and tenant-selection fact patterns.",
    ],
    blocks: [
      {
        heading: "Management serves the owner's lawful objectives",
        body:
          "A property manager works to protect and operate property for an owner. Revenue, occupancy, maintenance, records, and tenant relations can all appear in a management question.",
      },
      {
        heading: "A lease creates possession, not ownership",
        body:
          "The tenant receives a possessory interest for a defined term. Keep the leasehold interest separate from the owner's title interest when comparing answer choices.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A manager markets a unit and screens applicants. The exam may be testing the manager's duties and fair-housing constraints, not simply who the owner prefers.",
    },
    traps: [
      "Calling a tenant an owner because the tenant has possession.",
      "Treating management decisions as exempt from fair-housing rules.",
    ],
    terms: [
      ["Leasehold estate", "A tenant's right to possess property for a stated period."],
      ["Property manager", "A person who operates property for an owner under an agreement."],
      ["Operating statement", "A report used to track property income and expenses."],
    ],
  },
  {
    id: "transfer-title",
    number: "13",
    category: "National foundation",
    title: "Transfer of title, deeds, and closing",
    duration: "6 min",
    outlineSection: "Transfer of Title",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Title questions require you to separate ownership, evidence of ownership, recording, and the documents used to transfer property.",
    objectives: [
      "Separate title from a deed.",
      "Recognize why recording matters.",
      "Read closing questions for the document, party, and sequence involved.",
    ],
    blocks: [
      {
        heading: "A deed transfers; title describes ownership",
        body:
          "A deed is the written instrument used to convey an interest in real property. Title refers to the ownership rights themselves. The words are related but are not interchangeable.",
      },
      {
        heading: "Recording protects notice and priority",
        body:
          "Recording questions usually test notice, priority, or public records. The answer is rarely just 'to make the deed valid' without considering what the question asks about third parties.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "Two buyers claim rights from different transactions. The facts about notice and recording tell you which title concept the exam wants you to use.",
    },
    traps: [
      "Using deed and title as synonyms.",
      "Assuming recording solves every title issue by itself.",
    ],
    terms: [
      ["Deed", "A written instrument used to convey an interest in real property."],
      ["Title", "The legal ownership rights in property."],
      ["Recording", "Placing a document in the public land-record system."],
    ],
  },
  {
    id: "practice-real-estate",
    number: "14",
    category: "National foundation",
    title: "Practice of real estate and professional conduct",
    duration: "6 min",
    outlineSection: "Practice of Real Estate",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "This area turns everyday brokerage activity into exam questions about advertising, fair housing, ethics, records, and client service.",
    objectives: [
      "Identify professional-conduct issues inside a business scenario.",
      "Recognize advertising and representation clues.",
      "Separate lawful client service from misleading or discriminatory conduct.",
    ],
    blocks: [
      {
        heading: "Read the business fact for the compliance issue",
        body:
          "An advertisement, referral, listing, conversation, or record can look ordinary until one phrase creates the real test issue. Ask what the licensee communicated, omitted, or promised.",
      },
      {
        heading: "Fair housing is operational, not optional",
        body:
          "The exam can test fair-housing principles through marketing, steering, screening, or service decisions. Focus on the conduct described and the protected basis implicated by the facts.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A listing ad uses language aimed at attracting or discouraging a certain kind of person. The question is testing the wording's effect, not whether the home itself is desirable.",
    },
    traps: [
      "Treating advertising as casual language with no professional consequence.",
      "Assuming client preference makes discriminatory conduct acceptable.",
    ],
    terms: [
      ["Steering", "Influencing a person's housing choice based on a protected characteristic."],
      ["Blockbusting", "Inducing sales by suggesting demographic change or its consequences."],
      ["Misrepresentation", "A false statement or misleading omission about a material fact."],
    ],
  },
  {
    id: "real-estate-calculations",
    number: "15",
    category: "Math lab",
    title: "Real estate calculations without panic",
    duration: "8 min",
    outlineSection: "Real Estate Calculations",
    sourceReference: PSI_SOURCE,
    sourceUrl: PSI_BULLETIN_URL,
    summary:
      "Math becomes manageable when you label the unit, write the relationship, and solve one step at a time.",
    objectives: [
      "Set up commissions, area, ratios, and prorations clearly.",
      "Use the numbers the question gives instead of adding assumptions.",
      "Check whether the answer has the right unit and direction.",
    ],
    blocks: [
      {
        heading: "Translate words into a relationship",
        body:
          "Before touching a calculator, name what the question wants: a percentage, a dollar amount, an area, a share of an expense, or a loan amount. Then write the relationship in a simple equation.",
      },
      {
        heading: "Sanity-check the result",
        body:
          "A commission should be smaller than the sale price. A percentage should be between zero and one hundred. A proration should match the period and party stated in the problem. These checks catch many exam errors.",
      },
    ],
    scenario: {
      title: "Exam lens",
      body:
        "A seller pays a stated commission percentage on a stated sale price. Multiply the correct base by the percentage, then check that your answer is a dollar amount that makes sense.",
    },
    traps: [
      "Using a percentage as a whole number instead of a decimal when needed.",
      "Skipping the unit check after calculating.",
    ],
    terms: [
      ["Proration", "Dividing an expense or income item between parties according to time or another stated basis."],
      ["Commission", "Compensation calculated from a transaction amount under an agreement."],
      ["Loan-to-value ratio", "A loan amount compared with a stated property value or price."],
    ],
  },
];

export const LEARN_GROUPS = [
  {
    id: "illinois",
    title: "Illinois core",
    description: "Start with the state-specific rules that make Illinois different.",
  },
  {
    id: "national",
    title: "National foundation",
    description: "Build the nationwide real estate concepts behind the National Broker portion.",
  },
  {
    id: "math",
    title: "Math lab",
    description: "Practice the calculations that become easy when the setup is clear.",
  },
];

export const getLessonById = (lessonId) =>
  LEARN_CURRICULUM.find((lesson) => lesson.id === lessonId);

export const CURRICULUM_TERM_COUNT = LEARN_CURRICULUM.reduce(
  (total, lesson) => total + lesson.terms.length,
  0
);
